import React from 'react';
import { Alert, Button, Col, Row, Stack } from 'react-bootstrap';
import { getPreferredEventTileDescription } from 'cb-model';
import { DEFAULT_EVENT_IMAGE } from 'cb-unified';
import { displayDateTimeToMinute } from 'gossamer-unified';
import { EventTile, EventTileProps } from 'shared-front-end';
import { EventAudienceDetailsSection } from './EventAudienceDetailsSection';
import { EventBasicDetailsSection } from './EventBasicDetailsSection';
import { EventBookingDetailsSection } from './EventBookingDetailsSection';
import { EventDuringAfterSection } from './EventDuringAfterSection';
import { EventHostingDetailsSection } from './EventHostingDetailsSection';
import { EventPaidDetailsSection } from './EventPaidDetailsSection';
import { EventProfileSection } from './EventProfileSection';
import { EventErrors, EventFormValues } from './eventDetailsFormTypes';
import {
  buildEventFormErrors,
  hasPaidTickets,
  updateEventFormValue,
  validateEventFormField,
} from './eventFormValidation';
import { EventTypeOptions } from 'cb-model';

export interface EventFormProps {
  submitLabel: string;
  submitting: boolean;
  value: EventFormValues;
  institutionId: number;
  isUpdate?: boolean;
  onCancel?: () => void;
  onChange: (next: EventFormValues | ((prev: EventFormValues) => EventFormValues)) => void;
  onSubmit: () => void;
  submitActions?: React.ReactNode;
}

export const EventForm = (props: EventFormProps) => {
  const lockDateTimes = !!props.isUpdate && (props.value.tickets || []).some((ticket) => Number(ticket.price || 0) > 0);
  const lockPaidEventChanges = lockDateTimes;
  const userChangedPaidEventRef = React.useRef(false);
  const [isPaidEvent, setIsPaidEvent] = React.useState<boolean>(() => hasPaidTickets(props.value.tickets));

  const valueRef = React.useRef<EventFormValues>(props.value);
  React.useEffect(() => {
    valueRef.current = props.value;
  }, [props.value]);

  React.useEffect(() => {
    if (userChangedPaidEventRef.current) return;
    setIsPaidEvent(hasPaidTickets(props.value.tickets));
  }, [props.value]);

  const updateValue = React.useCallback(
    (updater: (prev: EventFormValues) => EventFormValues) => {
      const nextValue = updateEventFormValue(valueRef.current, updater);
      valueRef.current = nextValue;
      props.onChange(nextValue);
    },
    [props.onChange],
  );

  const eventTileProps = React.useMemo((): EventTileProps => {
    const eventTypeLabel = EventTypeOptions.find((o) => o.id === props.value.type)?.label || props.value.type || '';
    const ticketsLeft = (props.value.tickets || []).reduce((prev, ticket) => prev + Number(ticket.quantity || 0), 0);
    const startDate = props.value.start_date ? displayDateTimeToMinute(props.value.start_date) : undefined;

    return {
      eventUrl: '',
      eventType: eventTypeLabel,
      title: props.value.title || 'My training session',
      image: props.value.image || DEFAULT_EVENT_IMAGE,
      markdownDescription: props.value.markdown_description || undefined,
      plainDescription: getPreferredEventTileDescription(props.value) || 'Description text',
      ticketsLeft,
      startDate,
    };
  }, [props.value]);

  React.useEffect(() => {
    if (props.isUpdate) return;
    if (props.value.booking_method !== 'careerbook' || (props.value.booking_url || '').length > 0) {
      updateValue((prev) => ({
        ...prev,
        booking_method: 'careerbook',
        booking_url: '',
      }));
    }
  }, [props.isUpdate, props.value.booking_method, props.value.booking_url, updateValue]);

  React.useEffect(() => {
    if (props.value.type !== 'on-demand' || !props.value.repetition) return;
    updateValue((prev) => ({ ...prev, repetition: undefined }));
  }, [props.value.repetition, props.value.type, updateValue]);

  const errors = React.useMemo(() => buildEventFormErrors(props.value, { isPaidEvent }), [props.value, isPaidEvent]);

  const [fieldErrors, setFieldErrors] = React.useState<EventErrors>({});

  const setField = React.useCallback(
    <K extends keyof EventFormValues>(key: K, next: EventFormValues[K]) => {
      updateValue((prev) => ({ ...prev, [key]: next }));
    },
    [updateValue],
  );

  const handleDetailsChange = <K extends keyof EventFormValues>(key: K, next: any) => {
    setField(key, next);
  };

  const handleIsPaidEventChange = React.useCallback(
    (next: boolean) => {
      userChangedPaidEventRef.current = true;
      setIsPaidEvent(next);
      if (!next) {
        updateValue((prev) => ({
          ...prev,
          tickets: (prev.tickets || []).map((ticket) => ({ ...ticket, price: 0 })),
        }));
      }
    },
    [updateValue],
  );

  const validateForm = (params: {
    eventDetails: EventFormValues | undefined;
    setErrors: React.Dispatch<React.SetStateAction<EventErrors>>;
    key: keyof EventFormValues;
  }) => {
    return validateEventFormField({
      value: valueRef.current,
      setErrors: params.setErrors,
      key: params.key,
      isPaidEvent,
    });
  };

  return (
    <Row>
      <Col xs={12} xl={8}>
        <Stack gap={3}>
          {errors.length > 0 && (
            <Alert variant="danger">
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Please fix the following:</div>
              <ul style={{ marginBottom: 0 }}>
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </Alert>
          )}

          <EventProfileSection
            eventDetails={props.value}
            handleDetailsChange={handleDetailsChange}
            institutionId={props.institutionId}
          />

          <EventPaidDetailsSection
            eventDetails={props.value}
            institutionId={props.institutionId}
            isPaidEvent={isPaidEvent}
            lockPaidEventChanges={lockPaidEventChanges}
            onIsPaidEventChange={handleIsPaidEventChange}
            handleDetailsChange={handleDetailsChange}
          />

          <EventBasicDetailsSection
            eventDetails={props.value}
            handleDetailsChange={handleDetailsChange}
            errors={fieldErrors}
            setErrors={setFieldErrors}
            institutionId={props.institutionId}
            validateForm={validateForm}
          />

          <EventHostingDetailsSection
            eventDetails={props.value}
            handleDetailsChange={handleDetailsChange}
            errors={fieldErrors}
            setErrors={setFieldErrors}
            institutionId={props.institutionId}
            isUpdate={props.isUpdate}
            lockDateTimes={lockDateTimes}
            validateForm={validateForm}
          />

          <EventBookingDetailsSection
            eventDetails={props.value}
            handleDetailsChange={handleDetailsChange}
            errors={fieldErrors}
            setErrors={setFieldErrors}
            institutionId={props.institutionId}
            isPaidEvent={isPaidEvent}
            lockPaidEventChanges={lockPaidEventChanges}
            validateForm={validateForm}
          />

          <EventAudienceDetailsSection
            eventDetails={props.value}
            handleDetailsChange={handleDetailsChange}
            errors={fieldErrors}
            setErrors={setFieldErrors}
            validateForm={validateForm}
          />

          <EventDuringAfterSection
            eventDetails={props.value}
            handleDetailsChange={handleDetailsChange}
            errors={fieldErrors}
            setErrors={setFieldErrors}
            institutionId={props.institutionId}
            validateForm={validateForm}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            {props.onCancel && (
              <Button variant="outline-secondary" onClick={props.onCancel} disabled={props.submitting}>
                Cancel
              </Button>
            )}
            {props.submitActions || (
              <Button variant="primary" onClick={props.onSubmit} disabled={props.submitting || errors.length > 0}>
                {props.submitLabel}
              </Button>
            )}
          </div>
        </Stack>
      </Col>

      <Col xl={4} className="d-none d-xl-block">
        <div style={{ position: 'sticky', top: 12, width: '21rem' }}>
          <EventTile {...eventTileProps} />
        </div>
      </Col>
    </Row>
  );
};
